/* ============================================================================
   EASTEAM quick view — open it immediately
   ----------------------------------------------------------------------------
   WHY THIS EXISTS
   The base theme does not open the quick view when you click. It runs a
   stopwatch. In assets/theme.js, initQuickViewAction / quickPopUpSkelton:

     1. drop quickViewPreLoadGif (a spinner) into .popup-body-inner
     2. setTimeout 500ms  — before the popup is even shown
     3. fadeIn(500)       — desktop fade
     4. fetch the section — ~0.3s, the only part that is real work
     5. setTimeout 500ms  — before the fetched markup is injected

   That is roughly 1.8 seconds of mostly manufactured waiting, and the spinner
   is what shows during it. None of it is CSS and none of it is a setting — the
   numbers are literals inside a 254KB minified theme file.

   WHAT THIS DOES INSTEAD
   Takes the click on the capture phase, before theme.js's own delegate, and:
     · opens the popup on the same tick, so the ivory box is there instantly
     · fills it the moment the section arrives, with no spinner and no timers
     · prefetches on hover / touch-start, so by the time most people click the
       markup is already in memory and it is genuinely instant

   theme.js is left completely alone. Remove this file and the theme's own slow
   path takes over again; nothing breaks.

   CLOSING is still the theme's: .popup-close and .popup-background carry
   data-side-drawer-close, bound at page load, and they work regardless of who
   opened the popup. The handler below is only a fallback.

   2026-09-24 FIX — SECOND QUICK VIEW OPENED INVISIBLE
   The theme's close is a jQuery fadeOut that leaves `visibility:hidden` inline
   on the popup (and finishes with display:none ~500ms later). show() set
   display and opacity but not visibility, so after the first close every quick
   view opened as an invisible box that still locked the page scroll. show()
   now stops any running theme animation first and sets visibility too, and
   re-asserts itself once or twice in case she reopens inside the theme's
   ~500ms close timer; hide() clears it.

   LOAD ORDER: this file, then ea-quickview.js, whose MutationObserver notices
   the injected markup and wires the quick view up.
   ==========================================================================*/
(function () {
  'use strict';

  var SECTION = 'product-quick-view';
  var cache = {};
  var inflight = {};

  function urlFor(el) {
    var href = el.getAttribute('href');
    if (!href) { var a = el.closest('a[href]'); if (a) href = a.getAttribute('href'); }
    if (!href) return null;
    try { href = new URL(href, location.origin).pathname; } catch (e) { href = href.split('?')[0]; }
    if (href.indexOf('/products/') === -1) return null;
    return href + '?section_id=' + SECTION;
  }

  function load(url) {
    if (cache[url]) return Promise.resolve(cache[url]);
    if (inflight[url]) return inflight[url];
    var p = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('quick view ' + r.status); return r.text(); })
      .then(function (t) {
        var doc = new DOMParser().parseFromString(t, 'text/html');
        var sec = doc.querySelector('.shopify-section') || doc.body;
        cache[url] = sec.innerHTML;
        delete inflight[url];
        return cache[url];
      })
      .catch(function (e) { delete inflight[url]; throw e; });
    inflight[url] = p;
    return p;
  }

  function popup() { return document.querySelector('.quickview-popup'); }

  var wanted = false;
  function paintOpen(pop) {
    // finish any fade the theme's own close left running, or it lands after us
    if (window.jQuery) { try { window.jQuery(pop).stop(true, true); } catch (e) {} }
    pop.style.display = 'block';
    pop.style.opacity = '1';
    pop.style.visibility = 'visible';
    pop.classList.add('popup--visible');
    document.body.classList.add('no-scroll', 'quickview-open');
  }

  function show() {
    var pop = popup();
    if (!pop) return null;
    wanted = true;
    paintOpen(pop);
    /* the theme's close also schedules a plain timer (~500ms) that hides the
       popup at the end of its fade. If she reopens inside that window, the
       timer lands after us — so re-assert twice while it could still fire.
       Timers, not an observer: an observer on the popup's own style would feed
       on the writes it makes (see the ea-notify freeze, 2026-09-23). */
    [300, 650].forEach(function (ms) {
      setTimeout(function () { if (wanted) paintOpen(pop); }, ms);
    });
    return pop;
  }

  function hide() {
    var pop = popup();
    wanted = false;
    if (!pop) return;
    pop.classList.remove('popup--visible');
    pop.style.display = '';
    pop.style.opacity = '';
    pop.style.visibility = '';
    document.body.classList.remove('no-scroll', 'quickview-open');
  }

  function open(url, trigger) {
    var pop = show();
    if (!pop) return;
    var host = pop.querySelector('.popup-body-inner');
    if (!host) return;

    /* an empty ivory box for the few hundred ms before the markup lands, rather
       than a spinner. With the hover prefetch this is usually skipped. */
    if (!cache[url]) host.innerHTML = '';

    load(url).then(function (html) {
      host.innerHTML = html;
    }).catch(function (e) {
      hide();
      try { console.warn('[ea-quickview-open]', e); } catch (x) {}
      var a = trigger && trigger.closest('a[href]');
      if (a) location.href = a.getAttribute('href');
    });
  }

  /* CAPTURE phase — must beat theme.js's own bubble-phase delegate */
  document.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target.closest('.quickView-action-link') : null;
    if (!t) return;
    var url = urlFor(t);
    if (!url) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    t.classList.remove('disabled');
    open(url, t);
  }, true);

  /* warm it on intent so the click has nothing to wait for */
  ['pointerenter', 'touchstart'].forEach(function (ev) {
    document.addEventListener(ev, function (e) {
      var t = e.target.closest ? e.target.closest('.quickView-action-link') : null;
      if (!t) return;
      var url = urlFor(t);
      if (url && !cache[url] && !inflight[url]) load(url).catch(function () {});
    }, true);
  });

  /* fallback close — the theme's own data-side-drawer-close still runs too */
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var pop = popup();
    if (!pop || !pop.classList.contains('popup--visible')) return;
    if (e.target.closest('.popup-close') || e.target.closest('.popup-background')) hide();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var pop = popup();
    if (pop && pop.classList.contains('popup--visible')) hide();
  });
})();
